import React, { useState, useEffect } from 'react';
import { Table, Button, Upload, message, Space } from 'antd';
import { UploadOutlined, PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import generateLabel from '../orders/label_templates/TJ_SF';

const PrinterPage = () => {
  const [loading, setLoading] = useState(false);
  const [printerOrders, setPrinterOrders] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    showSizeChanger: true,
    showTotal: (total) => `共 ${total} 条订单`
  });

  const fetchPrinterOrders = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/orders/printer/printerorders/', {
        params: {
          page: params.page || pagination.current,
          page_size: params.pageSize || pagination.pageSize
        }
      });
      setPrinterOrders(response.data.results);
      setPagination({
        ...pagination,
        total: response.data.count,
        current: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize
      });
    } catch (error) {
      console.error('Error fetching printer orders:', error);
      message.error('获取打印订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrinterOrders();
  }, []); // Fetch on mount

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/orders/printer/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success(`成功导入 ${response.data.count} 条订单`);
      fetchPrinterOrders(); // Refresh the list after upload
      return false; // Prevent default upload behavior
    } catch (error) {
      console.error('Upload error:', error);
      message.error('导入失败: ' + (error.response?.data?.error || '未知错误'));
      return false;
    }
  };

  const handleTableChange = (newPagination) => {
    fetchPrinterOrders({
      page: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  const handlePrint = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要打印的订单');
      return;
    }

    try {
      setLoading(true);
      message.loading({ content: '准备打印...', key: 'batchPrintLoading' });

      // Get the selected printer orders first
      const selectedPrinterOrders = printerOrders.filter(order => selectedRowKeys.includes(order.id));
      
      // Find the corresponding order IDs in the main orders table using order numbers
      const orderSearchPromises = selectedPrinterOrders.map(async printerOrder => {
        try {
          const response = await axios.get('/api/orders/search/', {
            params: { search: printerOrder.order_number }
          });
          
          if (!response.data || response.data.length === 0) {
            message.error(`订单 ${printerOrder.order_number} 未找到`);
            return null;
          }
          
          return response.data[0].id; // Return the ID of the found order
        } catch (error) {
          console.error(`Error searching for order ${printerOrder.order_number}:`, error);
          return null;
        }
      });
      
      const orderIds = (await Promise.all(orderSearchPromises)).filter(id => id !== null);
      
      if (orderIds.length === 0) {
        message.error({ content: '没有找到有效的订单进行打印', key: 'batchPrintLoading' });
        setLoading(false);
        return;
      }
      
      if (orderIds.length < selectedPrinterOrders.length) {
        message.warning(`已选择 ${selectedPrinterOrders.length} 个订单，但只有 ${orderIds.length} 个可以打印`);
      }
      
      // Record print action in backend with the found order IDs
      await axios.post('/api/orders/batch_print/', {
        order_ids: orderIds
      });

      // Get selected orders data with additional fields
      const selectedOrders = await Promise.all(
        printerOrders
          .filter(order => selectedRowKeys.includes(order.id))
          .map(async order => {
            try {
              // Fetch complete order data using search endpoint
              const response = await axios.get('/api/orders/search/', {
                params: { search: order.order_number }
              });
              
              // Check if any order was found
              if (!response.data || response.data.length === 0) {
                throw new Error(`Order ${order.order_number} not found in search results`);
              }
              
              const completeOrder = response.data[0]; // Get first match
              return {
                ...order,
                dest_route_label: completeOrder.dest_route_label,
                coding_mapping: completeOrder.coding_mapping,
                two_dimension_code: completeOrder.two_dimension_code
              };
            } catch (error) {
              console.error(`Error fetching data for order ${order.order_number}:`, error);
              message.error(`订单 ${order.order_number} 未找到或无法获取完整数据`);
              return null; // Return null for orders that couldn't be found
            }
          })
      );
      
      // Filter out null values (orders that weren't found)
      const validOrders = selectedOrders.filter(order => order !== null);
      
      if (validOrders.length === 0) {
        message.error({ content: '没有找到有效的订单进行打印', key: 'batchPrintLoading' });
        setLoading(false);
        return;
      }
      
      if (validOrders.length < selectedOrders.length) {
        message.warning(`已选择 ${selectedOrders.length} 个订单，但只有 ${validOrders.length} 个可以打印`);
      }

      // Generate labels using the same function as OrderListItem
      const labelCanvases = await Promise.all(
        validOrders.map(async order => {
          // Create a temporary div for the shipping label
          const tempDiv = document.createElement('div');
          tempDiv.style.width = '100mm';
          tempDiv.style.height = '150mm';
          tempDiv.style.padding = '0';
          tempDiv.style.position = 'absolute';
          tempDiv.style.left = '-9999px';
          tempDiv.style.backgroundColor = 'white';
          tempDiv.style.boxSizing = 'border-box';

          // Generate label HTML
          const labelHtml = await generateLabel({
            ...order,
            recipient_name: order.new_recipient || order.recipient,
            recipient_phone: order.new_phone || order.phone,
            recipient_address: order.new_address || order.address,
            order_number: order.order_number,
            express_number: order.express_number,
            port: 'TJ', // Set port to TJ
            courier_service: 'SF' // Set courier to SF
          });
          
          if (!labelHtml) return null;
          
          tempDiv.innerHTML = labelHtml;
          document.body.appendChild(tempDiv);

          // Wait for rendering
          await new Promise(resolve => setTimeout(resolve, 300));

          // Capture the content as an image
          const canvas = await html2canvas(tempDiv, {
            scale: 4,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: 378,
            height: 567,
            onclone: (clonedDoc) => {
              const clonedDiv = clonedDoc.querySelector('div');
              if (clonedDiv) {
                clonedDiv.style.width = '378px';
                clonedDiv.style.height = '567px';
              }
            }
          });

          document.body.removeChild(tempDiv);
          return canvas;
        })
      );

      const validCanvases = labelCanvases.filter(canvas => canvas !== null);
      if (validCanvases.length === 0) {
        message.error({ content: '面单生成失败', key: 'batchPrintLoading' });
        return;
      }

      // Convert canvases to data URLs
      const dataUrls = validCanvases.map(canvas => 
        canvas.toDataURL('image/jpeg', 0.95)
      );

      // Create a function to print a single label
      const printSingleLabel = (dataUrl, index, total) => {
        return new Promise((resolve) => {
          const printWindow = window.open('', '_blank');
          if (!printWindow) {
            message.error({ content: '请允许弹出窗口以进行打印', key: 'batchPrintLoading' });
            resolve(false);
            return;
          }
          
          // Write the HTML for a single label
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <style>
                  @page { size: 100mm 150mm; margin: 0; }
                  body { margin: 0; padding: 0; }
                  .label-container { 
                    width: 100mm; 
                    height: 150mm;
                  }
                  img { 
                    width: 100%; 
                    height: 100%; 
                    display: block; 
                  }
                </style>
              </head>
              <body>
                <div class="label-container">
                  <img src="${dataUrl}" />
                </div>
                <script>
                  window.onload = () => {
                    setTimeout(() => {
                      window.print();
                      setTimeout(() => {
                        window.close();
                        window.opener.postMessage('printed', '*');
                      }, 100);
                    }, 100);
                  };
                </script>
              </body>
            </html>
          `);
          printWindow.document.close();
          
          // Listen for the message that printing is done
          window.addEventListener('message', function handler(event) {
            if (event.data === 'printed') {
              window.removeEventListener('message', handler);
              resolve(true);
            }
          });
          
          // Fallback in case the message event doesn't fire
          setTimeout(() => resolve(true), 2000);
        });
      };
      
      // Print labels one by one
      message.loading({ content: '正在打印...', key: 'batchPrintLoading' });
      
      // Use a for loop with await to print one label at a time
      for (let i = 0; i < dataUrls.length; i++) {
        await printSingleLabel(dataUrls[i], i, dataUrls.length);
        // Small delay between print jobs
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      message.success({ content: '打印请求已发送', key: 'batchPrintLoading' });
      fetchPrinterOrders(); // Refresh the list to update printed_at timestamps

    } catch (error) {
      console.error('Print error:', error);
      message.error({ content: '打印失败: ' + (error.response?.data?.error || error.message), key: 'batchPrintLoading' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 80,
    },
    {
      title: '快递单号',
      dataIndex: 'express_number',
      key: 'express_number',
      width: 120,
    },
    // {
    //   title: '原收件人',
    //   dataIndex: 'recipient',
    //   key: 'recipient',
    //   width: 80,
    // },
    // {
    //   title: '原电话',
    //   dataIndex: 'phone',
    //   key: 'phone',
    //   width: 120,
    // },
    // {
    //   title: '原地址',
    //   dataIndex: 'address',
    //   key: 'address',
    //   width: 200,
    // },
    // {
    //   title: '新收件人',
    //   dataIndex: 'new_recipient',
    //   key: 'new_recipient',
    //   width: 80,
    // },
    // {
    //   title: '新电话',
    //   dataIndex: 'new_phone',
    //   key: 'new_phone',
    //   width: 120,
    // },
    // {
    //   title: '新地址',
    //   dataIndex: 'new_address',
    //   key: 'new_address',
    //   width: 200,
    // },    
    {
      title: '收件人',
      dataIndex: 'new_recipient',
      key: 'new_recipient',
      width: 80,
    },
    {
      title: '电话',
      dataIndex: 'new_phone',
      key: 'new_phone',
      width: 120,
    },
    {
      title: '地址',
      dataIndex: 'new_address',
      key: 'new_address',
      width: 200,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '打印时间',
      dataIndex: 'printed_at',
      key: 'printed_at',
      width: 150,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Upload
            beforeUpload={handleUpload}
            showUploadList={false}
            accept=".xlsx,.xls"
          >
            <Button icon={<UploadOutlined />}>导入订单</Button>
          </Upload>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={selectedRowKeys.length === 0}
          >
            打印订单
          </Button>
        </Space>
      </div>
      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={columns}
        dataSource={printerOrders}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 1500, y: 'calc(100vh - 200px)' }}
        size="small"
      />
    </div>
  );
};

export default PrinterPage;
